import { Button, FormControl, FormHelperText, Grid, Input, InputLabel, Typography } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import EventIcon from '@material-ui/icons/Event';
import TimerIcon from '@material-ui/icons/Timer';
import cn from 'classnames';
import DatePicker from 'material-ui-pickers/DatePicker';
import moment from 'moment';
import ModifyingInfoModal from 'Components/EditOrder/comps/ModifyingInfoModal.component';
import {
  UI_MODIFYING_ORDER_DATE_INFO_MODAL_CANCEL,
  UI_MODIFYING_ORDER_DATE_INFO_MODAL_CONTINUE,
  UI_MODIFYING_ORDER_DATE_INFO_MODAL_SWITCH,
  UI_MODIFYING_ORDER_DATE_INFO_MODAL_CLOSE,
} from 'State/Store/UI/UI.types';
import { ORDER_EDIT_DATE_INFO_ALREADY_CONFIRMED_ON_THIS_DEVICE } from 'Constants/Order/Order.constants';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import withServices from '../../Contexts/Services/WithServices.context';
import docketsActionCreators from '../../State/Store/Dockets/Dockets.actions';
import filtersActionCreators from '../../State/Store/Filters/Filters.actions';
import getFiltersSelectedAccount from '../../State/Store/Filters/Selectors/FiltersSelectedAccount.selector';
import uiActionCreators from '../../State/Store/UI/UI.actions';
import { UI_DATE_TIME_SELECTOR_SELECT_DATE_TIME, UI_MODIFY_ORDER_DATE_TIME_SELECTOR_SELECT_DATE_TIME } from '../../State/Store/UI/UI.types';
import userProfileSelector from '../../State/Store/UserProfile/Selectors/UserProfileCurrent.selector';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import Modal from '../Modal/Modal.component';
import Spinner from '../Spinner/Spinner.component';
import placeOrderStyles from './PlaceOrder.component.styles';
import PlaceOrderErrorMessage from './PlaceOrderErrorMessage.component';
import isSecondNonWeekendDayFromToday from './utils/isSecondNonWeekendDayFromToday';
import isThirdNonWeekendDayFromToday from './utils/isThirdNonWeekendDayFromToday';
import InfoIcon from '@material-ui/icons/Info';

/**
 * DateTimeSelector
 * @param {any} props The props
 * @returns {React.Component} The component
 */
const DateTimeSelector = ({ actions, classes, services, onChange, defaultDateTime, tradingRegion, dateDisabled, isEdit, initialValue, order, width }) => {
  const [datesLoading, setDatesLoading] = useState(false);
  const [dates, setDates] = useState(null);
  const [error, setError] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isFirstChange, setIsFirstChange] = useState(true);
  const [showAvailableStartTimes, setShowAvailableStartTimes] = useState(false);
  const [showAvailableStartDates, setShowAvailableStartDates] = useState(false);
  const [timeSelectorOpen, setTimeSelectorOpen] = useState(false);
  const [timesAvailable, setTimesAvailable] = useState([]);
  const [originalTime, setOriginalTime] = useState(null);
  const [originalDate, setOriginalDate] = useState(null);
  const [dateInfoModalConfirmed, setDateInfoModalConfirmed] = useState(false);
  const [dateInfoModalOpen, setDateInfoModalOpen] = useState(false);
  const dateInfoModalCallbackRef = useRef();
  const [noTimesAvailable, setNoTimesAvailable] = useState(false);

  /**
   * Returns times to be displayed
   * @param {Object} momentDate moment object
   * @returns {Object} hours array of available hours
   */
  const generateHours = (momentDate) => {
    const hours = [];
    if (
      // if two days from now
      momentDate.isSame(
        moment()
          .add(2, `days`)
          .startOf(`day`),
        `day`,
      ) ||
      // if 3 days from now
      momentDate.isSame(
        moment()
          .add(3, `days`)
          .startOf(`day`),
        `day`,
      ) ||
      // if today is thursday then monday afternoon hours
      (moment().weekday() === 4 &&
        moment()
          .add(1, `weeks`)
          .startOf(`isoWeek`)
          .isSame(momentDate, `day`)) ||
      // if today is friday then monday and tuesday afternoon hours
      (moment().weekday() === 5 &&
        (moment()
          .add(1, `weeks`)
          .startOf(`isoWeek`)
          .isSame(momentDate, `day`) ||
          moment()
            .add(1, `weeks`)
            .startOf(`isoWeek`)
            .add(1, `day`)
            .isSame(momentDate, `day`))) ||
      // if today is saturday then tuesday and wednesday afternoon hours
      (moment().weekday() === 6 &&
        (moment()
          .add(1, `weeks`)
          .startOf(`isoWeek`)
          .add(1, `day`)
          .isSame(momentDate, `day`) ||
          moment()
            .add(1, `weeks`)
            .startOf(`isoWeek`)
            .add(2, `day`)
            .isSame(momentDate, `day`)))
    ) {
      // in two days hours
      for (let i = 12; i < 16; i += 1) {
        for (let j = 0; j < 60; j += 15) {
          hours.push({ hour: i, minute: j });
        }
      }
      hours.push({ hour: 16, minute: 0 });
    } else if (momentDate.weekday() === 6) {
      // saturday selected
      for (let i = 6; i < 12; i += 1) {
        for (let j = 0; j < 60; j += 15) {
          hours.push({ hour: i, minute: j });
        }
      }
      hours.push({ hour: 12, minute: 0 });
    } else {
      // normal hours
      for (let i = 6; i < 16; i += 1) {
        for (let j = 0; j < 60; j += 15) {
          hours.push({ hour: i, minute: j });
        }
      }
      hours.push({ hour: 16, minute: 0 });
    }
    return hours;
  };

  /**
   * Returns number of working days between now and selectedDate.
   * Working day is any day that is not saturday, sunday and is not public holiday
   * @param {Object} selectedDate moment object
   * @param {Array} holidayDates array of moment objects
   * @returns {number} number of working days
   */
  const getWorkingDaysUntil = (selectedDate, holidayDates) => {
    const day = moment().startOf(`day`);

    const selectedDateClone = selectedDate.clone().startOf(`day`);
    let count = 0;
    while (!selectedDateClone.isSame(day, `day`)) {
      day.add(1, 'days');
      if (
        day.weekday() !== 6 &&
        day.weekday() !== 0 &&
        !holidayDates.find((holiday) => {
          return holiday.isSame(day, 'day');
        })
      ) {
        count++;
      }
    }
    return count - 1;
  };

  /**
   * Returns initial minute
   * @param {number} minute minute provided
   * @returns {number} minute
   */
  const getInitialMinute = (minute) => {
    if (minute % 15) {
      if (minute < 15) {
        return 15;
      }
      if (minute < 30) {
        return 30;
      }
      if (minute < 45) {
        return 45;
      }
      return 0;
    }
    return minute;
  };

  /**
   * Returns initial hour
   * @param {number} hour minhourute provided
   * @param {number} minute minute provided
   * @returns {number} minute
   */
  const getInitialHour = (hour, minute) => {
    if (minute > 45) {
      return hour + 1;
    }
    return hour;
  };

  /**
   * Returns times to be displayed for edit order
   * @param {Object} momentDate moment object, its currently selected date
   * @returns {Object} hours array of available hours
   */
  const generateEditHours = (momentDate) => {
    const hours = [];
    // -1 as to cater for one next day that is never available
    const numberOfWorkingDays = getWorkingDaysUntil(momentDate, dates) - 1;
    const momentOrderOriginalDateTime = moment(defaultDateTime.selectedDateTime);
    const isTomorrow = moment()
      .startOf('day')
      .add(1, 'days')
      .isSame(momentOrderOriginalDateTime.clone().startOf('day'));
    const isDayAfterTomorrow = moment()
      .startOf('day')
      .add(2, 'days')
      .isSame(momentOrderOriginalDateTime.clone().startOf('day'));

    // generates same or later hours what original order time is, for normal days so until 4pm
    const generateSameOrLaterHours = (initialHour, initialMinute) => {
      if (initialHour > 16 || (initialHour === 16 && initialMinute)) {
        setNoTimesAvailable(true);
        return;
      } else if (initialHour === 16 && !initialMinute) {
        hours.push({ hour: 16, minute: 0 });
        return;
      }
      // if (initialHour < 12) {
      for (let j = initialMinute; j < 60; j += 15) {
        hours.push({ hour: initialHour, minute: j });
      }
      for (let i = initialHour + 1; i < 16; i += 1) {
        for (let j = 0; j < 60; j += 15) {
          hours.push({ hour: i, minute: j });
        }
      }
      // }

      hours.push({ hour: 16, minute: 0 });
    };

    // generates same or later hours what original order time is but for weekend so until 12pm only
    const generateWeekendSameOrLaterHours = (initialHour, initialMinute) => {
      if (initialHour > 12 || (initialHour === 12 && initialMinute)) {
        setNoTimesAvailable(true);
        return;
      }
      // if (initialHour < 12) {
      for (let j = initialMinute; j < 60; j += 15) {
        hours.push({ hour: initialHour, minute: j });
      }
      for (let i = initialHour + 1; i < 12; i += 1) {
        for (let j = 0; j < 60; j += 15) {
          hours.push({ hour: i, minute: j });
        }
      }
      // }
      hours.push({ hour: 12, minute: 0 });
    };

    const isSaturdayOrSunday =
      momentOrderOriginalDateTime
        .clone()
        .startOf('day')
        .weekday() === 6 ||
      momentOrderOriginalDateTime
        .clone()
        .startOf('day')
        .weekday() === 0;

    // original order is tomorrow or day after tomorrow, only hours that are after what the original order hours are and the date selected has not been changed
    if (
      (isTomorrow || isDayAfterTomorrow) &&
      !isDateModified({
        day: momentDate.date(),
        month: momentDate.month(),
        year: momentDate.year(),
        originalDay: momentOrderOriginalDateTime.date(),
        originalMonth: momentOrderOriginalDateTime.month(),
        originalYear: momentOrderOriginalDateTime.year(),
      })
    ) {
      const initialHour =
        momentOrderOriginalDateTime.get(`hour`) < 6 ? 6 : getInitialHour(momentOrderOriginalDateTime.get(`hour`), momentOrderOriginalDateTime.get(`minute`));
      // adjust minute so its 0,15,30 or 45 not e.g. 37
      const initialMinute = momentOrderOriginalDateTime.get(`hour`) < 6 ? 0 : getInitialMinute(momentOrderOriginalDateTime.get(`minute`));

      if (isSaturdayOrSunday) {
        generateWeekendSameOrLaterHours(initialHour, initialMinute);
      } else {
        generateSameOrLaterHours(initialHour, initialMinute);
      }
    } else if (numberOfWorkingDays < 2 && momentDate.weekday() !== 6 && momentDate.weekday() !== 0) {
      // we also checking or saturday here as order might have been placed on saturday (outside of CX order can also be placed on sunday) so if it is then we need to display saturday hours
      const initialHour = 12;
      const initialMinute = 0;
      for (let j = initialMinute; j < 60; j += 15) {
        hours.push({ hour: initialHour, minute: j });
      }
      // generate rest of available hours
      const untilHour = 16;

      for (let i = initialHour + 1; i < untilHour; i += 1) {
        for (let j = 0; j < 60; j += 15) {
          hours.push({ hour: i, minute: j });
        }
      }
      hours.push({ hour: untilHour, minute: 0 });
    } else if (
      momentDate.weekday() === 6 ||
      momentDate.weekday() === 0
      // no need for this as its checked in first conditional above
      // &&
      // !momentDate.isSame(
      //   moment()
      //     .add(1, `days`)
      //     .startOf(`day`),
      //   `day`,
      // )
    ) {
      // when editing order saturdays are not selectable but when placing order you can select saturday and morning hours (outside of CX order can also be placed on sunday). If already saturday/sunday is order day then we allow to select morning hours only
      for (let i = 6; i < 12; i += 1) {
        for (let j = 0; j < 60; j += 15) {
          hours.push({ hour: i, minute: j });
        }
      }
      hours.push({ hour: 12, minute: 0 });
    } else {
      // normal hours
      for (let i = 6; i < 16; i += 1) {
        for (let j = 0; j < 60; j += 15) {
          hours.push({ hour: i, minute: j });
        }
      }
      hours.push({ hour: 16, minute: 0 });
    }
    return hours;
  };

  /**
   * Returns first available date set to 10am
   * @returns {Object} moment object
   */
  const getFirstAvailableDate = () => {
    // if in 2 days is sunday then set to monday (so its 3rd day) 1pm
    if (
      moment()
        .add(2, `days`)
        .weekday() === 0
    ) {
      return moment()
        .add(3, `days`)
        .startOf(`day`)
        .add(12, `hours`);
    }
    // if in 2 days is saturday then we allow to monday 12pm-6pm
    if (
      moment()
        .add(2, `days`)
        .weekday() === 6
    ) {
      // set to 1pm in 2 days
      return moment()
        .add(4, `days`)
        .startOf(`day`)
        .add(12, `hours`);
    }
    return moment()
      .add(2, `days`)
      .startOf(`day`)
      .add(12, `hours`);
  };

  const [selectedDateTime, setSelectedDateTime] = useState(
    defaultDateTime.selectedDateTime ? moment(defaultDateTime.selectedDateTime) : getFirstAvailableDate(),
  );
  useEffect(() => {
    // request list of dates from api
    setDatesLoading(true);
    (async () => {
      try {
        const loadedDates = await services.orderService.getDatesDetails(tradingRegion);
        setDates(loadedDates.map((el) => moment(el)));

        // use below to add holidays
        // setDates(
        //   loadedDates
        //     .map((el) => moment(el))
        //     .concat([
        //       moment()
        //         .startOf(`day`)
        //         .add(5, 'days'),
        //       moment()
        //         .startOf(`day`)
        //         .add(4, 'days'),
        //     ]),
        // );

        // when trading region is changed reset selected date/time as this might not be available in this region e.g. region might have holiday that time
        let day = null;
        if (isEdit) {
          day = moment(defaultDateTime.selectedDateTime);
          if (initialValue) {
            setSelectedDateTime(moment(initialValue));
          }
          setOriginalTime({ hour: day.get(`hour`), minute: day.get(`minute`) });
          setOriginalDate({ day: day.get(`date`), month: day.get(`month`), year: day.get(`year`) });
        } else {
          day = defaultDateTime.selectedDateTime ? moment(defaultDateTime.selectedDateTime) : getFirstAvailableDate();
          if (
            !!loadedDates.find((el) => moment(el).isSame(day, `day`)) ||
            moment(day).weekday() === 0 ||
            moment(day).isBefore(moment().add(1, `days`)) ||
            moment(day).isAfter(moment().add(4, `weeks`))
          ) {
            setError(true);
            onChange({ selectedDateTime: null, isValid: false, isFirstChange });
          } else {
            const dt = defaultDateTime.selectedDateTime ? defaultDateTime.selectedDateTime : getFirstAvailableDate().toISOString();
            const details = {
              year: moment(dt).year(),
              day: moment(dt).date(),
              month: moment(dt).month(),
              hour: moment(dt).hour(),
              minute: moment(dt).minute(),
            };
            onChange({
              selectedDateTime: dt,
              details,
              isValid: defaultDateTime.selectedDateTime ? defaultDateTime.isValid : true,
              isFirstChange,
            });
            setSelectedDateTime(defaultDateTime.selectedDateTime ? moment(defaultDateTime.selectedDateTime) : getFirstAvailableDate());
            setIsValid(defaultDateTime.selectedDateTime ? defaultDateTime.isValid : true);
            setError(false);
          }
        }
      } catch (ex) {
        setError(true);
        onChange({ selectedDateTime: null, isValid: false, isFirstChange });
      }
      setIsFirstChange(false);
      setDatesLoading(false);
    })();
  }, [tradingRegion]);

  useEffect(() => {
    const checkDontShowAgain = async () => {
      const result = await services.storageService.getItem(ORDER_EDIT_DATE_INFO_ALREADY_CONFIRMED_ON_THIS_DEVICE);
      if (result) {
        setDateInfoModalConfirmed(true);
      }
    };
    checkDontShowAgain();
  }, [dateInfoModalOpen]);

  /**
   * Sets currently selected dateTime and informs parent about changes
   * @param {Object} dateTime moment object
   * @returns {void} void
   */
  const onDateTimeHandler = (dateTimeOriginal) => {
    actions.uiTriggered({
      payload: {
        data: {
          name: `dateTimeSelectorSelectDateTime`,
        },
      },
      type: isEdit ? UI_MODIFY_ORDER_DATE_TIME_SELECTOR_SELECT_DATE_TIME : UI_DATE_TIME_SELECTOR_SELECT_DATE_TIME,
    });

    const dateTime = dateTimeOriginal.clone();

    if (
      // ########### additional checks, no need as UI is already blocking it
      // if its before 12pm less than 2 days from now then fail (this is blocked via UI)
      // dateTime.isBefore(
      //   moment()
      //     .startOf(`day`)
      //     .add(2, `days`)
      //     .add(12, `hours`),
      // ) ||
      // if its saturday and two days from now, fail (this is blocked via UI)
      // (dateTime.isSame(
      //   moment()
      //     .add(2, `days`)
      //     .startOf(`day`),
      //   `day`,
      // ) &&
      //   dateTime.weekday() === 6) ||
      // ###########
      // its public holiday day
      dates.find((el) => moment(el).isSame(dateTime, `day`))
    ) {
      // display message as selected prior to 2 or 3 days midday
      // check if new selected date is saturday. User won';t be able to select holidays or sundays via UI
      onChange({ selectedDateTime: dateTime.toISOString(), isValid: false });
      // onChange({ selectedDateTime: dateTime.toISOString(), isValid: false });
      // setIsValid(false);
      setIsValid(false);
    } else {
      if (!isEdit && dateTime.weekday() === 6 && ((dateTime.hour() === 12 && dateTime.minute() != 0) || dateTime.hour() > 12)) {
        // automatically adjust change selected hours
        dateTime.hour(10);
        dateTime.minute(0);
      } else if (
        !isEdit &&
        // if its thursday and NEXT WEEK monday is selected (as any monday/tuesday week can be selected) is selected and its morning hours, then reset to 12pm
        ((moment().weekday() === 4 &&
          moment()
            .add(1, `weeks`)
            .startOf(`isoWeek`)
            .isSame(dateTime, `day`) &&
          dateTime.hour() < 12) ||
          // if its friday and NEXT WEEK monday or tuesday is selected (as any monday/tuesday week can be selected) is selected and its morning hours, then reset to 12pm
          (moment().weekday() === 5 &&
            (moment()
              .add(1, `weeks`)
              .startOf(`isoWeek`)
              .isSame(dateTime, `day`) ||
              moment()
                .add(1, `weeks`)
                .startOf(`isoWeek`)
                .add(1, `day`)
                .isSame(dateTime, `day`)) &&
            dateTime.hour() < 12) ||
          // if its saturday and NEXT WEEK monday/tuesday/wednesday is selected is selected and its morning hours, then reset to 12pm
          (moment().weekday() === 6 &&
            (moment()
              .add(1, `weeks`)
              .startOf(`isoWeek`)
              .isSame(dateTime, `day`) ||
              moment()
                .add(1, `weeks`)
                .startOf(`isoWeek`)
                .add(1, `day`)
                .isSame(dateTime, `day`) ||
              moment()
                .add(1, `weeks`)
                .startOf(`isoWeek`)
                .add(2, `day`)
                .isSame(dateTime, `day`)) &&
            dateTime.hour() < 12))
      ) {
        // automatically adjust change selected hours
        dateTime.hour(12);
        dateTime.minute(0);
      } else if (isEdit) {
        // the dates been changed so now we need to check if the currently set time is available for this date, if not, display error message
        const availableHours = generateEditHours(dateTime);
        const selectedHour = dateTime.hour();
        const selectedMinute = dateTime.minute();
        const details = { year: dateTime.year(), day: dateTime.date(), month: dateTime.month(), hour: dateTime.hour(), minute: dateTime.minute() };
        if (!availableHours.find((el) => el.hour === selectedHour && el.minute === selectedMinute)) {
          setIsValid(false);

          onChange({
            selectedDateTime: dateTime.toISOString(),
            details,
            isDateTimeCombinationValid: false,
          });
          setTimeSelectorOpen(false);
          setSelectedDateTime(dateTime);
          return;
        }
        onChange({
          selectedDateTime: dateTime.toISOString(),
          details,
          isDateTimeCombinationValid: true,
        });
        setIsValid(true);
        setTimeSelectorOpen(false);
        setSelectedDateTime(dateTime);
        return;
      }
      const details = { year: dateTime.year(), day: dateTime.date(), month: dateTime.month(), hour: dateTime.hour(), minute: dateTime.minute() };
      onChange({
        selectedDateTime: dateTime.toISOString(),
        details,
        isValid: true,
      });
      setIsValid(true);
      setError(false);
    }

    setTimeSelectorOpen(false);
    setSelectedDateTime(dateTime);
  };

  /**
   * Renders dateTime selector
   * @returns {component} component
   */
  const renderDateSelector = () => {
    const momentSelected = moment(selectedDateTime);
    let fieldClass = null;
    if (isEdit && !dateDisabled) {
      fieldClass =
        originalDate &&
        isDateModified({
          day: momentSelected.date(),
          month: momentSelected.month(),
          year: momentSelected.year(),
          originalDay: originalDate.day,
          originalMonth: originalDate.month,
          originalYear: originalDate.year,
        })
          ? classes.touchedField
          : classes.editableField;
    }

    /**
     * Renders custom input field
     * @param {Object} props props
     * @returns {component} component
     */
    const renderInput = (props) => {
      /* eslint-disable react/prop-types */
      const { onClick, value, disabled, inputProps } = props;
      return (
        <FormControl fullWidth>
          <InputLabel htmlFor="delivery-date">Delivery Date</InputLabel>
          <Input
            className={fieldClass}
            label="Date"
            fullWidth
            id="delivery-date"
            startAdornment={
              <InputAdornment className={classes.calendarIcon} position="start">
                <EventIcon />
              </InputAdornment>
            }
            type="text"
            onClick={!dateDisabled ? onClick : () => {}}
            value={value}
            disabled={dateDisabled}
            {...inputProps}
          />
          {!dateDisabled && (
            <FormHelperText>
              Select an available date from the calendar.{` `}
              <span className={classes.helperText} onClick={() => setShowAvailableStartDates(true)}>
                Can&apos;t select the date you want?
              </span>
            </FormHelperText>
          )}
        </FormControl>
      );
    };

    return dates ? (
      <DatePicker
        format="ddd MMMM Do"
        TextFieldComponent={renderInput}
        renderDay={(day, selectedDate, dayInCurrentMonth, element) => {
          const isToday = moment().isSame(day, `day`);
          const isSaturday = moment(day).weekday() === 6;
          const isSecondNonWeekendDay = isSecondNonWeekendDayFromToday(day);
          const isThirdNonWeekendDay = isThirdNonWeekendDayFromToday(day);
          const testidAttrs = {
            'data-testid-order-date-picker-saturday': isSaturday ? 'data-testid-order-date-picker-saturday' : undefined,
            'data-testid-order-date-picker-second-day': isSecondNonWeekendDay ? 'data-testid-order-date-picker-second-day' : undefined,
            'data-testid-order-date-picker-third-day': isThirdNonWeekendDay ? 'data-testid-order-date-picker-third-day' : undefined,
          };
          if (isToday) {
            return <span className={classes.today}>{element}</span>;
          }
          return <span {...testidAttrs}>{element}</span>;
        }}
        shouldDisableDate={(day) => {
          if (isEdit) {
            const dayBeingRendered = moment(day).weekday();
            const today = moment().startOf(`day`);
            const todayWeekday = today.weekday();
            const orderDate = moment(defaultDateTime.selectedDateTime);

            // also dont disable the orderdate that is currently selected
            if (moment(day).isSame(orderDate, 'day')) {
              return false;
            }

            return (
              // disable all saturday and sundays
              dayBeingRendered === 0 ||
              dayBeingRendered === 6 ||
              // if today is friday or saturday or sunday then monday is not available and can move to tuesday onwards only
              ((todayWeekday === 5 || todayWeekday === 6 || todayWeekday === 0) && dayBeingRendered === 1) ||
              // And the current date and +1 WD is not available (taking into consideration public holidays and AC 1 working days)
              moment(day).isBefore(today.add(2, `days`)) ||
              // Up to 4 weeks in advance
              moment(day).isAfter(today.add(4, `weeks`)) ||
              // disable holidays dates
              dates.find((el) => moment(el).isSame(day, `day`))
            );
          }

          // START HERE WITH QUESTION: if today is monday and wednesday is holiday, does STL goes to thuirsday?

          return (
            // uncomment and test below when editing days needs to be enablked
            // isEdit rules. Basically for next 2 days we need to allow to select times only after what is selected now
            // (isEdit &&
            //   (moment(day).isSame(moment().add(1, `days`), 'day') || moment(day).isSame(moment().add(2, `days`), 'day')) &&
            //   ((moment(day).weekday() === 6 && moment(day).get(`hour`) === 12) || moment(day).get(`hour`) === 16)) ||
            dates.find((el) => moment(el).isSame(day, `day`)) ||
            moment(day).weekday() === 0 ||
            moment(day).isBefore(moment().add(1, `days`)) ||
            moment(day).isAfter(moment().add(4, `weeks`)) ||
            // if saturday in two days then
            (moment(day).weekday() === 6 &&
              moment(day).isSame(
                moment()
                  .add(2, `days`)
                  .startOf(`day`),
                `day`,
              )) ||
            // if saturday in three days then
            (moment(day).weekday() === 6 &&
              moment(day).isSame(
                moment()
                  .add(3, `days`)
                  .startOf(`day`),
                `day`,
              ))
              || moment(day).isBetween(moment("2022/12/20", "YYYY/MM/DD"), moment("2023/01/11", "YYYY/MM/DD"))
          );
        }}
        onChange={(dateTime) => {
          const updateDateTime = () => {
            dateTime.hour(selectedDateTime.hour());
            dateTime.minute(selectedDateTime.minute());
            dateTime.second(selectedDateTime.second());
            onDateTimeHandler(dateTime);
          };
          if (
            !dateInfoModalConfirmed &&
            isEdit &&
            isDateModified({
              day: dateTime.date(),
              month: dateTime.month(),
              year: dateTime.year(),
              originalDay: originalDate.day,
              originalMonth: originalDate.month,
              originalYear: originalDate.year,
            })
          ) {
            setDateInfoModalOpen(true);
            dateInfoModalCallbackRef.current = () => {
              updateDateTime();
            };
          } else {
            updateDateTime();
          }
        }}
        value={selectedDateTime}
      />
    ) : null;
  };

  /**
   * Checks if date is modified
   * @param {Object} props props
   * @returns {boolean} is date modified
   */
  const isDateModified = ({ day, month, year, originalDay, originalMonth, originalYear }) => {
    return day !== originalDay || month !== originalMonth || year !== originalYear;
  };

  /**
   * Checks if time is modified
   * @param {Object} props props
   * @returns {boolean} is time modified
   */
  const isTimeModified = ({ hour, minute, originalHour, originalMinute }) => {
    return hour !== originalHour || minute !== originalMinute;
  };

  /**
   * Renders dateTime selector
   * @returns {component} component
   */
  const renderTimeSelector = () => {
    const momentSelected = moment(selectedDateTime);

    let fieldClass = null;
    if (isEdit) {
      fieldClass =
        originalTime &&
        isTimeModified({
          hour: momentSelected.hour(),
          minute: momentSelected.minute(),
          originalHour: originalTime.hour,
          originalMinute: originalTime.minute,
        })
          ? classes.touchedField
          : classes.editableField;
    }

    return dates ? (
      <FormControl fullWidth error={isEdit && !isValid}>
        <InputLabel htmlFor="delivery-time">Start Time</InputLabel>
        <Input
          className={fieldClass}
          onClick={() => {
            let time = selectedDateTime.clone();
            if (originalTime) {
              time.hour(originalTime.hour);
              time.minute(originalTime.minute);
            } else {
              time = selectedDateTime;
            }
            setTimesAvailable(isEdit ? generateEditHours(moment(selectedDateTime)) : generateHours(moment(selectedDateTime)));
            setTimeSelectorOpen(true);
          }}
          id="delivery-time"
          label="Start Time"
          fullWidth
          startAdornment={
            <InputAdornment className={classes.calendarIcon} position="start">
              <TimerIcon />
            </InputAdornment>
          }
          type="text"
          value={momentSelected.format(`hh:mm A`)}
        />
        {isEdit && !isValid ? (
          <FormHelperText>This time is not available for the selected date. You must update the start time before proceeding.</FormHelperText>
        ) : (
          <FormHelperText>
            {dateDisabled ? `Modify the time for your first load. ` : `Select an available time for your first load.`}
            {` `}
            <span className={classes.helperText} onClick={() => setShowAvailableStartTimes(true)}>
              Can&apos;t select the time you want?
            </span>
          </FormHelperText>
        )}
      </FormControl>
    ) : null;
  };

  const timesContent = (
    <React.Fragment>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2">
            {`Placing an order online has delivery date & time conditions as outlined below. If your preferred date or time is not available via Boral Connects, please `}
            <a className={classes.hyperlink} rel="noopener noreferrer" target="_blank" href="https://www.boral.com.au/locations?product_category%5B86%5D=86">
              Call Boral{!isWidthDown('sm', width) && order && order.details && order.details.boralTel ? ` (${order.details.boralTel})` : ''}
            </a>
            {` to ${isEdit ? `modify` : `place`} your order.`}
          </Typography>
        </Grid>
      </Grid>
      <Typography variant="body2">
        <ul className={classes.tipsList}>
          <li>
            <div className={classes.fakeBulletPoint}>»</div>
            {`Next Day: Not available`}
          </li>
          <li>
            <div className={classes.fakeBulletPoint}>»</div>
            {`In 2 - 3 business days: 12PM - 4PM`}
          </li>
          <li>
            <div className={classes.fakeBulletPoint}>»</div>
            {`In 4 - 30 business days: 6AM - 4PM`}
          </li>
          <li>
            <div className={classes.fakeBulletPoint}>»</div>
            {`Sat: 6AM - 12PM`}
          </li>
          <li>
            <div className={classes.fakeBulletPoint}>»</div>
            {`Sun & public holidays: Not available`}
          </li>
        </ul>
      </Typography>
    </React.Fragment>
  );

  let renderFields = (
    <React.Fragment>
      <Grid item xs={12}>
        {renderDateSelector()}
      </Grid>
      <Grid item xs={12}>
        {renderTimeSelector()}
      </Grid>
    </React.Fragment>
  );

  if (dateDisabled) {
    renderFields = (
      <React.Fragment>
        <Grid item xs={12}>
          {renderTimeSelector()}
        </Grid>
        <Grid item xs={12}>
          {renderDateSelector()}
        </Grid>
      </React.Fragment>
    );
  }

  const content = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2">The modified order will be moved to the new date with a new order number</Typography>
      </Grid>
    </Grid>
  );

  return (
    <React.Fragment>
      <ModifyingInfoModal
        title="Modifying Date Note"
        content={content}
        gtmTagContinue={UI_MODIFYING_ORDER_DATE_INFO_MODAL_CONTINUE}
        gtmTagCancel={UI_MODIFYING_ORDER_DATE_INFO_MODAL_CANCEL}
        gtmTagClose={UI_MODIFYING_ORDER_DATE_INFO_MODAL_CLOSE}
        gtmTagSwitch={UI_MODIFYING_ORDER_DATE_INFO_MODAL_SWITCH}
        localStorageFlag={ORDER_EDIT_DATE_INFO_ALREADY_CONFIRMED_ON_THIS_DEVICE}
        open={dateInfoModalOpen}
        onClose={() => setDateInfoModalOpen(false)}
        onContinue={() => {
          dateInfoModalCallbackRef.current();
          setDateInfoModalOpen(false);
        }}
      />
      <Modal
        content={
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {timesAvailable.find((el) => el.hour < 12) && (
                <React.Fragment>
                  <Typography variant="overline">Morning</Typography> <hr className={classes.keyLine} />
                </React.Fragment>
              )}
              <Grid container spacing={2}>
                {timesAvailable &&
                  timesAvailable
                    .filter((time) => time.hour < 12)
                    .map((time, i) => {
                      let hourDisplay = null;
                      if (time.hpur < 10) {
                        hourDisplay = `0${time.hour}`;
                      } else {
                        hourDisplay = `${time.hour}`;
                      }

                      let isSelected = false;
                      if (originalTime && originalTime.hour == time.hour && originalTime.minute == time.minute) {
                        isSelected = true;
                      }
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <Grid item xs={6} sm={3} key={i}>
                          <Button
                            className={isSelected ? classes.editableField : null}
                            onClick={() => {
                              const isoDate = moment(selectedDateTime);
                              isoDate.year(selectedDateTime.year());
                              isoDate.month(selectedDateTime.month());
                              isoDate.day(selectedDateTime.day());
                              isoDate.hour(time.hour);
                              isoDate.minute(time.minute);
                              isoDate.second(0);
                              onDateTimeHandler(isoDate);
                            }}
                          >
                            <TimerIcon className={cn(classes.timerButtonIcon, classes.opacity05)} />
                            {hourDisplay}:{time.minute < 10 ? `0${time.minute}` : time.minute}
                            {` `}AM
                          </Button>
                        </Grid>
                      );
                    })}
              </Grid>
              {timesAvailable.find((el) => el.hour > 11) && (
                <React.Fragment>
                  <Typography className={classes.marginTop2unit} variant="overline">
                    Afternoon
                  </Typography>
                  {` `}
                  <hr className={classes.keyLine} />
                </React.Fragment>
              )}
              <Grid container spacing={2}>
                {timesAvailable &&
                  timesAvailable
                    .filter((time) => time.hour > 11)
                    .map((time, i) => {
                      let hourDisplay = null;
                      if (time.hour === 12) {
                        hourDisplay = `12`;
                      } else if (time.hour > 12) {
                        hourDisplay = `${time.hour - 12}`;
                      }

                      let isSelected = false;
                      if (originalTime && originalTime.hour == time.hour && originalTime.minute == time.minute) {
                        isSelected = true;
                      }
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <Grid item xs={6} sm={3} className={classes.buttonItem} key={i}>
                          <Button
                            className={isSelected ? classes.editableField : null}
                            onClick={() => {
                              const isoDate = moment(selectedDateTime);
                              isoDate.year(selectedDateTime.year());
                              isoDate.month(selectedDateTime.month());
                              isoDate.day(selectedDateTime.day());
                              isoDate.hour(time.hour);
                              isoDate.minute(time.minute);
                              isoDate.second(0);
                              onDateTimeHandler(isoDate);
                            }}
                          >
                            <TimerIcon className={cn(classes.timerButtonIcon, classes.opacity05)} />
                            {hourDisplay}:{time.minute < 10 ? `0${time.minute}` : time.minute}
                            {` `}PM
                          </Button>
                        </Grid>
                      );
                    })}
              </Grid>
              <Typography variant="caption" className={classes.helpLink} onClick={() => setShowAvailableStartDates(true)}>
                I can&apos;t select the time I want
              </Typography>
            </Grid>
          </Grid>
        }
        onClose={() => setTimeSelectorOpen(false)}
        open={timeSelectorOpen}
        paperClasses={classes.timeSelector}
        title={
          <Grid container>
            <Grid item xs={12}>
              Select Start Time
            </Grid>
          </Grid>
        }
      />
      <Modal
        content={
          <Typography variant="body2">
            There are currently no start times available for this date via Boral Connects. Please{' '}
            <a className={classes.hyperlink} rel="noopener noreferrer" target="_blank" href="https://www.boral.com.au/locations?product_category%5B86%5D=86">
              Call Boral
            </a>{' '}
            to modify your order or select dfferent date for your delivery.
          </Typography>
        }
        footer={
          <Grid className={classes.textAlignRight} container justify="flex-end" spacing={2}>
            <Grid item>
              <Button
                className={classes.button}
                color="primary"
                onClick={() => {
                  setNoTimesAvailable(false);
                  setTimeSelectorOpen(false);
                }}
                variant="contained"
              >
                OK
              </Button>
            </Grid>
          </Grid>
        }
        paperClasses={classes.paperAutoHeight}
        maxWidth="sm"
        icon={<InfoIcon />}
        preventFullScreen
        onClose={() => {
          setNoTimesAvailable(false);
          setTimeSelectorOpen(false);
        }}
        open={noTimesAvailable}
        rightHeading={null}
        title={
          <Grid container>
            <Grid item xs={12}>
              Select Start Times
            </Grid>
          </Grid>
        }
      />
      <Modal
        content={timesContent}
        footer={
          <Grid className={classes.textAlignRight} container justify="flex-end" spacing={2}>
            <Grid item>
              <Button className={classes.button} color="primary" onClick={() => setShowAvailableStartDates(false)} variant="contained">
                OK
              </Button>
            </Grid>
          </Grid>
        }
        paperClasses={classes.paperAutoHeight}
        maxWidth="sm"
        icon={<InfoIcon />}
        preventFullScreen
        onClose={() => setShowAvailableStartDates(false)}
        open={!!showAvailableStartDates}
        rightHeading={null}
        title={
          <Grid container>
            <Grid item xs={12}>
              Delivery Dates &amp; Times
            </Grid>
          </Grid>
        }
      />
      <Modal
        content={timesContent}
        footer={
          <Grid className={classes.textAlignRight} container justify="flex-end" spacing={2}>
            <Grid item>
              <Button className={classes.button} color="primary" onClick={() => setShowAvailableStartTimes(false)} variant="contained">
                OK
              </Button>
            </Grid>
          </Grid>
        }
        paperClasses={classes.paperAutoHeight}
        maxWidth="sm"
        icon={<InfoIcon />}
        preventFullScreen
        onClose={() => setShowAvailableStartTimes(false)}
        open={!!showAvailableStartTimes}
        rightHeading={null}
        title={
          <Grid container>
            <Grid item xs={12}>
              Delivery Dates &amp; Times
            </Grid>
          </Grid>
        }
      />
      {datesLoading ? (
        <Spinner />
      ) : (
        <Grid container spacing={2}>
          {renderFields}
          {(!isValid || error) && (
            <Grid item xs={12}>
              {dates && dates.find((el) => moment(el).isSame(selectedDateTime, `day`)) ? (
                <PlaceOrderErrorMessage
                  message={
                    <Typography variant="body2" className={classes.errorText}>
                      There is an issue with the selected date or time (this is often caused by local public holidays). Please update the date to an available
                      start time to proceed with your order.
                    </Typography>
                  }
                />
              ) : (
                !isEdit && (
                  <PlaceOrderErrorMessage
                    message={
                      <Typography variant="body2" className={classes.errorText}>
                        There is an issue with online ordering at this time. Please{` `}
                        <a
                          className={classes.errorText}
                          rel="noopener noreferrer"
                          target="_blank"
                          href="https://www.boral.com.au/locations?product_category%5B86%5D=86"
                        >
                          Call Boral
                        </a>
                        {` `}
                        for more information and to place your order.
                      </Typography>
                    }
                  />
                )
              )}
            </Grid>
          )}
        </Grid>
      )}
    </React.Fragment>
  );
};

/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
DateTimeSelector.propTypes = {
  actions: PropTypes.object.isRequired,
  services: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  defaultDateTime: PropTypes.object,
  tradingRegion: PropTypes.string,
  dateDisabled: PropTypes.bool,
  isEdit: PropTypes.bool,
};

export default compose(
  withWidth(),
  withRouter,
  withServices,
  connect(
    (state) => ({
      listSettings: state.dockets ? state.dockets.listSettings : null,
      userInfo: userProfileSelector(state),
      currentDateTimeGlobal: getFiltersSelectedAccount(state),
    }),
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...uiActionCreators,
          ...filtersActionCreators,
          ...docketsActionCreators,
        },
        dispatch,
      ),
    }),
  ),
  withStyles(themeCreateInjectableStyles(placeOrderStyles), { withTheme: true }),
)(DateTimeSelector);
